import React from 'react';
import { Grid } from '@mui/material';
import { Button, Card, Chip, IconButton, Icons, Radio, Stack, TextField, Typography } from '@curves/ui-base';
import { PRICE_FIELDS } from '@Local/Product/priceFields';

/**
 * 單張規格卡片
 *
 * @param {object} props
 * @param {number} props.index 卡片索引（同時也是送出時 variants[] 的順序，供後端 0002 錯誤對應）
 * @param {object} props.variant 卡片資料
 * @param {boolean} props.isDefault 是否為目前選定的主規格
 * @param {boolean} props.canRemove 是否可刪除（已存在的規格不可刪除）
 * @param {boolean} [props.isVariantNameRequired=true] 規格名稱是否必填（只有一個規格時非必填）
 * @param {object} [props.errors] 欄位錯誤訊息（前端檢核與後端 0002 合併後的結果）
 * @param {(field: string, value: string) => void} props.onFieldChange
 * @param {() => void} props.onSelectDefault
 * @param {() => void} [props.onRemove]
 * @param {() => void} props.onApplyToAll
 */
const VariantCard = ({ index, variant, isDefault, canRemove, isVariantNameRequired = true, errors = {}, onFieldChange, onSelectDefault, onRemove, onApplyToAll }) => {
    // 產生指定欄位的 onChange，將輸入值回傳給父層
    const handleChange = (field) => (e) => onFieldChange(field, e.target.value);

    return (
        <Card className="p-3 mb-3">
            <Card.CardContent className="p-0">
                <Stack direction="row" justifyContent="space-between" alignItems="center" className="mb-2">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Radio checked={isDefault} onChange={onSelectDefault} />
                        <Typography className="mb-0">設為主規格</Typography>
                        {isDefault && <Chip label="主規格" size="small" color="primary" />}
                    </Stack>
                    {canRemove && (
                        <IconButton size="small" onClick={onRemove}>
                            <Icons.Trash fontSize="small" />
                        </IconButton>
                    )}
                </Stack>

                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField
                            label="規格名稱"
                            value={variant.variantName}
                            onChange={handleChange('variantName')}
                            isError={!!errors.variantName}
                            helperText={errors.variantName}
                            fullWidth
                            required={isVariantNameRequired}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="SKU"
                            value={variant.sku}
                            onChange={handleChange('sku')}
                            isError={!!errors.sku}
                            helperText={errors.sku}
                            fullWidth
                            required
                        />
                    </Grid>
                    {PRICE_FIELDS.map(({ field, label, tip, required }) => (
                        <Grid item xs={4} key={field}>
                            <TextField
                                label={label}
                                FormLabelTip={tip}
                                value={variant[field]}
                                onChange={handleChange(field)}
                                isError={!!errors[field]}
                                helperText={errors[field]}
                                fullWidth
                                required={required}
                            />
                        </Grid>
                    ))}
                    <Grid item xs={4}>
                        <TextField
                            label="單位個數"
                            value={variant.unitAmount}
                            onChange={handleChange('unitAmount')}
                            isError={!!errors.unitAmount}
                            helperText={errors.unitAmount}
                            fullWidth
                            required
                        />
                    </Grid>
                </Grid>

                <Stack direction="row" justifyContent="end" className="mt-2">
                    <Button variant="outlined" size="small" color="primary" onClick={onApplyToAll}>
                        套用到全部規格
                    </Button>
                </Stack>
            </Card.CardContent>
        </Card>
    );
};

export default VariantCard;
